/* The conversation's shared treatments: the raised card, the three buttons, a question's option, the
 * shimmering status line, the sunken notice and the two entrances.
 *
 * Canon: `docs/design/ai-buddy-2026-09-30/`, board O ("raised surface family") with M for the flow.
 * Every colour, shadow and duration is a token (`--buddy-*`, `--shadow-buddy-*` in
 * `tokens/colors.css`, `--dur-buddy-*` in `tokens/spacing.css`); sizes are layout.
 *
 * ── The primary is flat (owner, 29 October 2026) ─────────────────────────────────────────────
 * The canon drew the long buttons raised: a gradient fill, a top highlight and a dark bottom bevel.
 * On a full-width button that read as dated, so the primary is a gradient with no cast and no
 * bevel (`--buddy-primary-fill`, built from the brand's own gradient stops, so it follows the
 * trainer's brand). Since 4 November 2026 (owner) it is the app's one primary paint, the Pulpit
 * pill's diagonal gradient, and the composer's round send button is flat too. The secondary keeps
 * its raised treatment.
 *
 * The primary is NOT `shared/ui/emberButton`: composing that class and overriding it here would be
 * settled by chunk order, which differs between the dev server and the build. */

.card {
  background: var(--buddy-raised);
  border: var(--border-hairline) solid var(--buddy-edge);
  border-radius: var(--radius-20px);
  box-shadow: var(--shadow-buddy-card);
}

.button {
  display: inline-flex;
  flex: none;
  gap: var(--space-7px);
  align-items: center;
  height: 36px;
  padding: var(--space-0) var(--space-15px);
  font-family: var(--font-body);
  font-size: var(--fs-foot);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  white-space: nowrap;
  cursor: pointer;
  background: var(--buddy-raised);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-11px);
  box-shadow: var(--shadow-buddy-control);
  transition-duration: var(--dur-fast);
  transition-property: transform;
  transition-timing-function: var(--ease);
}

.button:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.button:active:not([aria-disabled='true']) {
  transform: translateY(1px);
}

.ghost {
  color: var(--text-2);
  background: transparent;
  border-color: transparent;
  box-shadow: none;
}

.primary {
  color: var(--on-accent-grad);
  background: var(--buddy-primary-fill);
  border-color: transparent;
  box-shadow: none;

  /* White on the accent clears 3:1 only as bold text (system spec amendment, 2 November 2026). */
  font-weight: var(--fw-semibold);
}

.small {
  height: 32px;
  padding: var(--space-0) var(--space-3);
  font-size: var(--fs-note);
  border-radius: var(--radius-sm);
}

/* Refusing, and still focusable: the reason it names is read with it (`aria-describedby`). */
.button[aria-disabled='true'] {
  cursor: default;
  opacity: 0.4;
}

/* One answer to a clarifying question: a native radio inside, so the group has one tab stop and
 * the arrows walk it. */
.option {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: var(--space-7px) var(--space-13px);
  line-height: var(--lh-hint);
  overflow-wrap: anywhere;
  font-size: var(--fs-foot);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  cursor: pointer;
  background: var(--buddy-raised);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-buddy-control);
  transition-duration: var(--dur-base);
  transition-property: color, background-color, border-color, box-shadow;
  transition-timing-function: var(--ease);
}

.option:hover {
  color: var(--text);
}

.optionOn,
.optionOn:hover {
  color: var(--bg);
  background: var(--text);
  border-color: var(--text);
  box-shadow: var(--shadow-buddy-chosen);
}

.option:has(input:focus-visible) {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

/* A line being worked on: the tertiary ink with a sheen running through it. */
.shimmer {
  color: transparent;
  background: var(--buddy-shimmer);
  background-clip: text;
  background-size: 200% 100%;
  animation-name: shimmer;
  animation-duration: var(--dur-buddy-shimmer);
  animation-timing-function: var(--ease-linear);
  animation-iteration-count: infinite;
}

.notice {
  display: flex;
  gap: var(--space-10px);
  align-items: flex-start;
  padding: var(--space-13px) var(--space-15px);
  font-size: var(--fs-foot);
  line-height: var(--lh-lede);
  color: var(--text-2);
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-md);
}

.notice b {
  color: var(--text);
}

.noticeGlyph {
  flex: none;
  width: 16px;
  height: 16px;
  margin-top: var(--space-1px);
}

/* A message rising in, and a card a beat slower, as the canon's `up` keyframe. They play when the
 * element is first drawn and never again: every message box is keyed by its turn. */
.rise {
  animation-name: rise;
  animation-duration: var(--dur-slow);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

.riseCard {
  animation-name: rise;
  animation-duration: var(--dur-buddy-card);
  animation-timing-function: var(--ease);
  animation-fill-mode: both;
}

/* A message already there at the thread's first paint (a resumed conversation) stands still. */
[data-still].rise,
[data-still] .rise,
[data-still] .riseCard {
  animation-name: none;
}

/* Work under way: the build log's running line, a file being read. */
.spinner {
  flex: none;
  width: 14px;
  height: 14px;
  border: var(--border-thick) solid var(--border-2);
  border-top-color: var(--text);
  border-radius: var(--radius-sm);
  animation-name: spin;
  animation-duration: var(--dur-buddy-spin);
  animation-timing-function: var(--ease-linear);
  animation-iteration-count: infinite;
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes shimmer {
  from {
    background-position: 200% 0;
  }

  to {
    background-position: -200% 0;
  }
}

@keyframes spin {
  to {
    transform: rotate(1turn);
  }
}
